import type { Nullable, RGBA, CanvasDrawImageSource } from "models-webgl2";
import { imageSourceToCanvas } from "utils/canvas/image-source-to-canvas";
import { getTrimmedImageMatrix } from "utils/canvas/get-trimmed-image-matrix";
import { loadImage } from "utils/helpers/load-image";
import { Vec2 } from "utils/math/vec2";
import { toRadians } from "utils/math/to-radians";
import { calcCropping } from "utils/math/calc-cropping";
import { MakeWorkerJobType, WorkerJob } from "utils/workers";
import { TextureComponent } from "../webgl2-component";
import { Webgl2 } from "../webgl2";
import { trimmingOverwhelm, trimWorkerExecutor } from "./texture.constants";

export class Texture {
  private readonly _gl: WebGL2RenderingContext;

  public readonly id: WebGLTexture;

  private _textureSize = Vec2.zero();
  public get textureSize(): Vec2 {
    return this._textureSize.clone();
  }

  private _hasData = false;
  public get hasData() {
    return this._hasData;
  }

  private _textureSource: CanvasDrawImageSource;
  public get textureSource(): CanvasDrawImageSource {
    return this._textureSource;
  }

  constructor(width: number, height: number);
  constructor(vec: Vec2);
  constructor(src?: CanvasDrawImageSource);
  constructor(a?: any, b?: any) {
    this._gl = Webgl2.gl;

    this.id = this._gl.createTexture() as WebGLTexture;

    if (!a) {
      this.loadPixelTexture();
    } else {
      if (a instanceof Vec2) {
        this.allocateTexture(a);
      } else if (typeof a === "number") {
        this.allocateTexture(a, b);
      } else {
        this.loadSource(a);
      }
    }
  }

  public bind() {
    this._gl.bindTexture(this._gl.TEXTURE_2D, this.id);
  }

  public unbind() {
    this._gl.bindTexture(this._gl.TEXTURE_2D, null);
  }

  public deleteTexture() {
    this._gl.deleteTexture(this.id);
  }

  public clone(withDelete = false): Texture {
    const size = this.textureSize;
    const clonedTexture = new Texture(size);

    Texture._textureComponent.transform.setSize(size.width, size.height);
    Texture._textureComponent.transform.setTranslation(0, 0);
    Texture._textureComponent.transform.setDegrees(0);

    clonedTexture.drawIntoTexture(() => {
      Webgl2.renderComponent(Texture._textureComponent, { texture: this });
    });

    clonedTexture._hasData = this._hasData;

    if (withDelete) this.deleteTexture();

    return clonedTexture;
  }

  public allocateTexture(width: number, height: number): void;
  public allocateTexture(vec: Vec2): void;
  public allocateTexture(a: any, b?: any) {
    try {
      const isVec = a instanceof Vec2;
      const width = isVec ? a.x : a;
      const height = isVec ? a.y : b;

      if (this.textureSize.equals(width, height)) return;

      this.bind();

      // this.gl.REPEAT | this.gl.CLAMP_TO_EDGE
      // this.gl.LINEAR | this.gl.NEAREST
      this._gl.texParameteri(this._gl.TEXTURE_2D, this._gl.TEXTURE_WRAP_S, this._gl.CLAMP_TO_EDGE);
      this._gl.texParameteri(this._gl.TEXTURE_2D, this._gl.TEXTURE_WRAP_T, this._gl.CLAMP_TO_EDGE);
      this._gl.texParameteri(this._gl.TEXTURE_2D, this._gl.TEXTURE_MIN_FILTER, this._gl.LINEAR);
      this._gl.texParameteri(this._gl.TEXTURE_2D, this._gl.TEXTURE_MAG_FILTER, this._gl.NEAREST);

      this._gl.texImage2D(
        this._gl.TEXTURE_2D,
        0,
        this._gl.RGBA,
        width,
        height,
        0,
        this._gl.RGBA,
        this._gl.UNSIGNED_BYTE,
        null,
      );

      this._textureSize.set(width, height);
      this._hasData = true;

      this.unbind();
    } catch (error) {
      this.unbind();

      console.error("defineTexture");
      console.error(error);
      // this._renderer.throwError(error);
    }
  }

  public loadSource(
    src: Nullable<CanvasDrawImageSource | string>,
    preloadPixelTexture = true,
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      preloadPixelTexture && this.loadPixelTexture();

      if (!src) return reject(new Error("No src passed"));

      if (typeof src === "string") {
        return loadImage(src, true)
          .then((image) => {
            this._onImageLoaded(image);
            resolve();
          })
          .catch((error) => {
            this.loadPixelTexture();
            reject(error);
          });
      } else if (src instanceof Image) {
        if (src.complete) {
          this._onImageLoaded(src);
          return resolve();
        }

        src.onerror = (error) => {
          this.loadPixelTexture();
          reject(error);
        };
        src.onload = () => {
          this._onImageLoaded(src);
          resolve();
        };
      } else {
        this._onImageLoaded(src);
        resolve();
      }
    });
  }

  public loadSyncSource(src: CanvasDrawImageSource) {
    this._onImageLoaded(src);
  }

  public loadPixelTexture(rgba?: RGBA) {
    this.bind();

    this._gl.texImage2D(
      this._gl.TEXTURE_2D,
      0,
      this._gl.RGBA,
      1,
      1,
      0,
      this._gl.RGBA,
      this._gl.UNSIGNED_BYTE,
      new Uint8ClampedArray(rgba ?? Texture.defaultPixelTextureColor),
    );

    this._textureSize.set(1, 1);
    this._hasData = false;

    this.unbind();
  }

  public texSubImage2D(
    xOffset: number,
    yOffset: number,
    width: number,
    height: number,
    data: number[],
  ) {
    this.bind();

    this._gl.texSubImage2D(
      this._gl.TEXTURE_2D,
      0,
      xOffset,
      yOffset,
      width,
      height,
      this._gl.RGBA,
      this._gl.UNSIGNED_BYTE,
      new Uint8ClampedArray(data),
    );

    this.unbind();
  }

  public drawIntoTexture(callback: VoidFunction) {
    Webgl2.bindTextureToFramebuffer(this, callback);
  }

  public textureToCanvas(): HTMLCanvasElement {
    return imageSourceToCanvas(this.readTexture());

    // TODO: investigate imageSourceToCanvas(Webgl2.canvas) blending issue
    // const prevViewport = Webgl2.camera.projection;
    // Webgl2.camera.save();
    //
    // const gl = Webgl2.gl;
    // const { width, height } = this.textureSize;
    //
    // // Setup block
    // Webgl2.setViewport(width, height);
    // Webgl2.camera.reset(width, height);
    //
    // Texture._material.setTexture("u_texture0", this);
    // Texture._rect.setSize(width, height);
    // Texture._rect.setTranslation(0, 0);
    // Texture._rect.setDegrees(0);
    //
    // gl.clearStencil(0);
    // gl.clearColor(0, 0, 0, 0);
    // gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT | gl.STENCIL_BUFFER_BIT);
    //
    // gl.enable(gl.BLEND);
    // gl.blendFunc(gl.SRC_ALPHA, gl.ONE);
    //
    // // Render block
    // Webgl2.renderShape(Texture._rect);
    //
    // const result = imageSourceToCanvas(Webgl2.canvas);
    //
    // Webgl2.setViewport(prevViewport);
    // Webgl2.camera.restore();
    //
    // gl.disable(gl.BLEND);
    //
    // return result;
  }

  public readTexture(): ImageData {
    const prevViewport = Webgl2.camera.projection;
    Webgl2.camera.save();

    const gl = Webgl2.gl;
    const { width, height } = this.textureSize;

    // Setup block
    Webgl2.setViewport(width, height);
    Webgl2.camera.reset(width, height);

    // Note: MUST be flipped due to readPixels read from left-bottom (Y-flipped)
    Texture._textureComponent.transform.setSize(width, -height);
    Texture._textureComponent.transform.setTranslation(0, height);
    Texture._textureComponent.transform.setDegrees(0);

    // Render block
    Webgl2.renderComponent(Texture._textureComponent, { texture: this });

    const result = new Uint8ClampedArray(width * height * 4);
    gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, result);

    Webgl2.setViewport(prevViewport);
    Webgl2.camera.restore();

    return new ImageData(result, width, height);
  }

  private _onImageLoaded(textureSource: CanvasDrawImageSource) {
    try {
      this.bind();
      this._textureSource = textureSource;
      // this.gl.REPEAT | this.gl.CLAMP_TO_EDGE
      // this.gl.LINEAR | this.gl.NEAREST
      this._gl.texParameteri(this._gl.TEXTURE_2D, this._gl.TEXTURE_WRAP_S, this._gl.CLAMP_TO_EDGE);
      this._gl.texParameteri(this._gl.TEXTURE_2D, this._gl.TEXTURE_WRAP_T, this._gl.CLAMP_TO_EDGE);
      this._gl.texParameteri(this._gl.TEXTURE_2D, this._gl.TEXTURE_MIN_FILTER, this._gl.LINEAR);
      this._gl.texParameteri(this._gl.TEXTURE_2D, this._gl.TEXTURE_MAG_FILTER, this._gl.LINEAR);

      this._gl.texImage2D(
        this._gl.TEXTURE_2D,
        0,
        this._gl.RGBA,
        this._gl.RGBA,
        this._gl.UNSIGNED_BYTE,
        textureSource,
      );

      // this._gl.texImage2D(
      //   this._gl.TEXTURE_2D,
      //   0,
      //   this._gl.RGBA,
      //   this.imageData.width,
      //   this.imageData.height,
      //   0,
      //   this._gl.RGBA,
      //   this._gl.UNSIGNED_BYTE,
      //   this.imageData.data,
      // );

      // this._gl.generateMipmap(this._gl.TEXTURE_2D);
      this._textureSize.set(textureSource.width, textureSource.height);
      this._hasData = true;

      this.unbind();
    } catch (error) {
      this.unbind();
      console.error("_onImageLoaded");
      console.error(error);
      // this._renderer.throwError(error);
    }
  }

  private static _textureComponent: TextureComponent;
  private static _trimWorker: MakeWorkerJobType<typeof trimWorkerExecutor, 4>;

  public static defaultPixelTextureColor: RGBA = [255, 0, 255, 255];

  public static initializeTextureData() {
    this._textureComponent = new TextureComponent();
    this._trimWorker = new WorkerJob(trimWorkerExecutor, 4);
  }

  public static resizeTexture(
    source: Texture,
    destination: Texture,
    width: number,
    height: number,
  ): Texture;
  public static resizeTexture(source: Texture, destination: Texture, vec: Vec2): Texture;
  public static resizeTexture(source: Texture, destination: Texture, a: any, b?: any): Texture {
    const isSameTexture = destination === source;
    if (isSameTexture) {
      source = destination.clone();
    }

    const isVec = a instanceof Vec2;
    const width = Math.abs(Math.round(isVec ? a.x : a));
    const height = Math.abs(Math.round(isVec ? a.y : b));

    Texture._textureComponent.transform.setSize(width, height);
    Texture._textureComponent.transform.setTranslation(0, 0);
    Texture._textureComponent.transform.setDegrees(0);

    destination.allocateTexture(width, height);
    destination.drawIntoTexture(() => {
      Webgl2.clear();
      Webgl2.renderComponent(Texture._textureComponent, { texture: source });
    });
    if (isSameTexture) source.deleteTexture();

    return destination;
  }

  public static cropTexture(
    source: Texture,
    destination: Texture,
    sx: number,
    sy: number,
    sw: number,
    sh: number,
  ): Texture {
    const isSameTexture = destination === source;
    if (isSameTexture) {
      source = destination.clone();
    }

    const { width, height } = source.textureSize;

    const cropping = calcCropping(width, height, sx, sy, sw, sh);

    if (!cropping.sw || !cropping.sh) return destination;

    Texture._textureComponent.transform.setSize(width, height);
    Texture._textureComponent.transform.setTranslation(-cropping.sx, -cropping.sy);
    Texture._textureComponent.transform.setDegrees(0);

    destination.allocateTexture(cropping.sw, cropping.sh);
    destination.drawIntoTexture(() => {
      Webgl2.clear();
      Webgl2.renderComponent(Texture._textureComponent, { texture: source });
    });
    if (isSameTexture) source.deleteTexture();

    return destination;
  }

  public static rotateTexture(
    source: Texture,
    destination: Texture,
    degrees: number,
    sizeOverride?: Vec2,
  ): Texture {
    const isSameTexture = destination === source;
    if (isSameTexture) {
      source = destination.clone();
    }

    const sizeToSetVec = sizeOverride ?? source.textureSize;
    const { width, height } = sizeToSetVec;

    const rads = toRadians(degrees);

    const cos = Math.abs(Math.cos(rads));
    const sin = Math.abs(Math.sin(rads));

    const newWidth = Math.ceil(width * cos + height * sin);
    const newHeight = Math.ceil(height * cos + width * sin);
    const newSize = new Vec2(newWidth, newHeight);

    Texture._textureComponent.transform.calcDimensions({
      position: newSize.getHalf(),
      size: sizeToSetVec,
      rotation: degrees,
      roundPosition: false,
    });

    destination.allocateTexture(newSize);
    destination.drawIntoTexture(() => {
      Webgl2.clear();
      Webgl2.renderComponent(Texture._textureComponent, { texture: source });
    });
    if (isSameTexture) source.deleteTexture();

    return destination;
  }

  public static trimTexture(source: Texture): {
    trimmedTexture: Texture;
    trimMatrix: ReturnType<typeof getTrimmedImageMatrix>;
  } {
    const prevViewport = Webgl2.camera.projection;
    Webgl2.camera.save();

    const gl = Webgl2.gl;
    const { width, height } = source.textureSize;

    // Setup block
    Webgl2.setViewport(width, height);
    Webgl2.camera.reset(width, height);

    Texture._textureComponent.transform.setSize(width, -height);
    Texture._textureComponent.transform.setTranslation(0, height);
    Texture._textureComponent.transform.setDegrees(0);

    // Render block
    Webgl2.renderComponent(Texture._textureComponent, { texture: source });

    const result = new Uint8ClampedArray(width * height * 4);
    gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, result);

    Webgl2.setViewport(prevViewport);
    Webgl2.camera.restore();

    const trimMatrix = getTrimmedImageMatrix(result, width, height);

    const croppedTexture = Texture.cropTexture(
      source,
      new Texture(),
      trimMatrix.x,
      trimMatrix.y,
      trimMatrix.width,
      trimMatrix.height,
    );

    return { trimmedTexture: croppedTexture, trimMatrix };
  }

  public static async trimTextureAsync(source: Texture): Promise<{
    trimmedTexture: Texture;
    trimMatrix: ReturnType<typeof getTrimmedImageMatrix>;
  }> {
    const prevViewport = Webgl2.camera.projection;
    Webgl2.camera.save();

    const gl = Webgl2.gl;
    const { width, height } = source.textureSize;

    // Setup block
    Webgl2.setViewport(width, height);
    Webgl2.camera.reset(width, height);

    Texture._textureComponent.transform.setSize(width, -height);
    Texture._textureComponent.transform.setTranslation(0, height);
    Texture._textureComponent.transform.setDegrees(0);

    // Render block
    Webgl2.renderComponent(Texture._textureComponent, { texture: source });

    if (width * height <= trimmingOverwhelm) {
      const result = new Uint8ClampedArray(width * height * 4);
      gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, result);

      Webgl2.setViewport(prevViewport);
      Webgl2.camera.restore();

      const trimMatrix = getTrimmedImageMatrix(result, width, height);

      const croppedTexture = Texture.cropTexture(
        source,
        new Texture(),
        trimMatrix.x,
        trimMatrix.y,
        trimMatrix.width,
        trimMatrix.height,
      );

      return { trimmedTexture: croppedTexture, trimMatrix };
    }

    const halfWidth = Math.floor(width / 2);
    const restWidth = width - halfWidth;
    const halfHeight = Math.floor(height / 2);
    const restHeight = height - halfHeight;

    const result1 = new Uint8ClampedArray(halfWidth * halfHeight * 4);
    gl.readPixels(0, 0, halfWidth, halfHeight, gl.RGBA, gl.UNSIGNED_BYTE, result1);

    const result2 = new Uint8ClampedArray(restWidth * halfHeight * 4);
    gl.readPixels(halfWidth, 0, restWidth, halfHeight, gl.RGBA, gl.UNSIGNED_BYTE, result2);

    const result3 = new Uint8ClampedArray(halfWidth * restHeight * 4);
    gl.readPixels(0, halfHeight, halfWidth, restHeight, gl.RGBA, gl.UNSIGNED_BYTE, result3);

    const result4 = new Uint8ClampedArray(restWidth * restHeight * 4);
    gl.readPixels(halfWidth, halfHeight, restWidth, restHeight, gl.RGBA, gl.UNSIGNED_BYTE, result4);

    Webgl2.setViewport(prevViewport);
    Webgl2.camera.restore();

    const results = await this._trimWorker.process([
      { data: result1, width: halfWidth, height: halfHeight },
      { data: result2, width: restWidth, height: halfHeight },
      { data: result3, width: halfWidth, height: restHeight },
      { data: result4, width: restWidth, height: restHeight },
    ]);

    const x02 = Math.min(results[0].x, results[2].x);
    const x13 = Math.min(results[1].x, results[3].x);
    const tX = x02 + x13;

    const y01 = Math.min(results[0].y, results[1].y);
    const y23 = Math.min(results[2].y, results[3].y);
    const tY = y01 + y23;

    const width02 = Math.max(results[0].width, results[2].width);
    const width13 = Math.max(results[1].width, results[3].width);
    const tWidth = width02 + width13;

    const height01 = Math.max(results[0].height, results[1].height);
    const height23 = Math.max(results[2].height, results[3].height);
    const tHeight = height01 + height23;

    const croppedTexture = Texture.cropTexture(source, new Texture(), tX, tY, tWidth, tHeight);

    return {
      trimmedTexture: croppedTexture,
      trimMatrix: { x: tX, y: tY, width: tWidth, height: tHeight },
    };
  }
}

export default Texture;
